/**
 * Panel de Gestión de Pacientes para Administradores.
 * Permite visualizar el censo completo de pacientes registrados, filtrar por municipio y laboratorio,
 * y consultar el historial clínico detallado (timeline) de cada persona.
 */

import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Apiurl } from "../../shared/services/apirest";
import AdminLayout from "./AdminLayout";
import bootstrap from "bootstrap/dist/js/bootstrap.bundle";

const AdminPatients = () => {
    const [pacientes, setPacientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [labFilter, setLabFilter] = useState("all");
    const [filterMunicipio, setFilterMunicipio] = useState("");
    const [sexFilter, setSexFilter] = useState("all");
    const [originFilter, setOriginFilter] = useState("all");
    const [sortOrder, setSortOrder] = useState("asc");
    const [selectedPaciente, setSelectedPaciente] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [loadingHistorial, setLoadingHistorial] = useState(false);
    const [currentUserRole, setCurrentUserRole] = useState("");
    const toastRef = useRef(null);
    const modalRef = useRef(null);

    /**
     * Carga el historial de casos clínicos de un paciente específico.
     * @param {Object} paciente - Objeto con los datos del paciente seleccionado.
     */
    const handleVerHistorial = async (paciente) => {
        setSelectedPaciente(paciente);
        setLoadingHistorial(true);
        const token = localStorage.getItem("token");
        const config = { headers: { Authorization: `Bearer ${token}` } };

        try {
            const res = await axios.get(`${Apiurl}casos/?paciente=${paciente.id}`, config);
            setHistorial(res.data);
            const modalEl = document.getElementById('historialModal');
            const bsModal = new bootstrap.Modal(modalEl);
            bsModal.show();
        } catch (err) {
            console.error("Error al cargar historial:", err);
            showToast("Error al cargar el historial del paciente.", "danger");
        } finally {
            setLoadingHistorial(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem("token");
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        setCurrentUserRole(userData.role || "");

        if (!token) {
            showToast("⚠️ Sesión no iniciada. Redirigiendo al inicio de sesión...", "warning");
            setTimeout(() => window.location.href = "/Login", 1500);
            setLoading(false);
            return;
        }

        const config = { headers: { Authorization: `Bearer ${token}` } };

        // Carga de la lista completa de pacientes
        axios.get(`${Apiurl}casos/pacientes/`, config)
            .then(res => {
                setPacientes(res.data);
                setLoading(false);
                if (res.data.length === 0) {
                    showToast("ℹ️ No hay pacientes registrados en el sistema.", "info");
                }
            })
            .catch(err => {
                console.error("Error al cargar pacientes:", err);
                setLoading(false);
                if (err.response?.status === 401) {
                    showToast("❌ Sesión expirada. Por favor, inicie sesión nuevamente.", "danger");
                    localStorage.clear();
                    setTimeout(() => window.location.href = "/Login", 2000);
                } else if (err.response?.status === 403) {
                    showToast("❌ No tiene permisos para acceder a esta información.", "danger");
                } else {
                    const errorMsg = err.response?.data?.detail || "Error al cargar los pacientes. Verifique su conexión.";
                    showToast(`❌ ${errorMsg}`, "danger");
                }
            });
    }, []);

    /**
     * Muestra notificaciones tipo Toast en la interfaz.
     */
    const showToast = (message, type = "success") => {
        const toastEl = toastRef.current;
        if (toastEl) {
            const bsToast = new bootstrap.Toast(toastEl);
            const toastBody = toastEl.querySelector(".toast-body");
            toastBody.textContent = message;
            toastEl.className = `toast align-items-center text-bg-${type} border`;
            bsToast.show();
        }
    };

    /**
     * Gestiona la exportación de datos de pacientes en diversos formatos.
     */
    const handleExport = async (format) => {
        const token = localStorage.getItem("token");
        if (!token) return;

        showToast(`⏳ Preparando descarga en formato ${format.toUpperCase()}...`, "info");

        try {
            const response = await axios.get(`${Apiurl}casos/export/?export_type=${format}&resource=pacientes`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            const extensions = { csv: 'csv', json: 'json', geojson: 'geojson', pdf: 'pdf', xlsx: 'xlsx' };
            link.setAttribute('download', `export_pacientes_geohcx_${new Date().getTime()}.${extensions[format]}`);

            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast(`✅ Archivo ${format.toUpperCase()} descargado con éxito.`, "success");
        } catch (err) {
            console.error("Error al exportar:", err);
            showToast("❌ Error al generar la exportación.", "danger");
        }
    };

    // Lógica de filtrado y ordenamiento memorizada
    const filteredPacientes = React.useMemo(() => {
        let result = [...pacientes];

        // 1. Búsqueda por texto (Nombre, Documento o Municipio)
        if (filter) {
            const term = filter.toLowerCase();
            result = result.filter(p =>
                (p.nombre || "").toLowerCase().includes(term) ||
                (p.documento || "").includes(filter) ||
                (p.municipio || "").toLowerCase().includes(term)
            );
        }

        // 2. Filtro por Laboratorio
        if (labFilter !== "all") {
            result = result.filter(p => p.laboratorio_nombre === labFilter);
        }

        // 3. Filtro por Municipio
        if (filterMunicipio) {
            result = result.filter(p => p.municipio === filterMunicipio);
        }

        // 4. Filtro por Sexo
        if (sexFilter !== "all") {
            result = result.filter(p => p.sexo === sexFilter);
        }

        // 5. Filtro por Origen (Laboratorio vs Investigación)
        if (originFilter !== "all") {
            result = result.filter(p => {
                const isResearch = p.codigo_proyecto_investigacion && p.codigo_proyecto_investigacion !== "";
                return originFilter === "investigacion" ? isResearch : !isResearch;
            });
        }

        // 6. Ordenamiento alfabético
        result.sort((a, b) => {
            const nameA = (a.nombre || "").toLowerCase();
            const nameB = (b.nombre || "").toLowerCase();
            if (sortOrder === "asc") return nameA.localeCompare(nameB);
            return nameB.localeCompare(nameA);
        });

        return result;
    }, [pacientes, filter, labFilter, filterMunicipio, sexFilter, originFilter, sortOrder]);


    // Estadísticas calculadas para el resumen superior
    const stats = {
        total: pacientes.length,
        masculino: pacientes.filter(p => p.sexo === "M").length,
        femenino: pacientes.filter(p => p.sexo === "F").length,
        ninos: pacientes.filter(p => p.edad < 15).length,
    };

    const [showExportMenu, setShowExportMenu] = useState(false);

    return (
        <AdminLayout
            breadcrumb="Panel de Administración / Pacientes"
            title="Gestión de Pacientes Registrados"
        >
            {/* Contenedor de Alertas (Toasts) */}
            <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 1055 }}>
                <div ref={toastRef} className="toast" role="alert" aria-live="assertive" aria-atomic="true">
                    <div className="d-flex">
                        <div className="toast-body fw-semibold"></div>
                        <button type="button" className="btn-close me-2 m-auto" data-bs-dismiss="toast" aria-label="Cerrar"></button>
                    </div>
                </div>
            </div>

            {/* Tarjetas de Resumen Estadístico */}
            <div className="row g-3 mb-4">
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <i className="bi bi-people-fill fs-2 text-primary me-3"></i>
                            <div>
                                <h4 className="m-0 fw-bold">{stats.total}</h4>
                                <small className="text-muted">Total Pacientes</small>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <i className="bi bi-gender-male fs-2 text-warning me-3"></i>
                            <div>
                                <h4 className="m-0 fw-bold">{stats.masculino}</h4>
                                <small className="text-muted">Masculinos</small>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <i className="bi bi-gender-female fs-2 text-info me-3"></i>
                            <div>
                                <h4 className="m-0 fw-bold">{stats.femenino}</h4>
                                <small className="text-muted">Femeninos</small>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <i className="bi bi-balloon-fill fs-2 text-success me-3"></i>
                            <div>
                                <h4 className="m-0 fw-bold">{stats.ninos}</h4>
                                <small className="text-muted">Menores ({"<"}15)</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Barra de Herramientas: Exportación y Búsqueda */}
            <div className="toolbar flex-wrap gap-3">
                <div className="d-flex align-items-center">
                    <div className="dropdown">
                        <button
                            className="btn btn-dark dropdown-toggle py-2 px-3 fw-semibold"
                            type="button"
                            onClick={() => setShowExportMenu(!showExportMenu)}
                        >
                            <i className="bi bi-download me-2"></i> Exportar
                        </button>
                        <ul className={`dropdown-menu border ${showExportMenu ? 'show' : ''}`} style={{ display: showExportMenu ? 'block' : 'none' }}>
                            <li><button className="dropdown-item py-2" onClick={() => { handleExport('xlsx'); setShowExportMenu(false); }}><i className="bi bi-file-earmark-excel-fill me-2 text-success"></i> Excel (.xlsx)</button></li>
                            <li><button className="dropdown-item py-2" onClick={() => { handleExport('csv'); setShowExportMenu(false); }}><i className="bi bi-filetype-csv me-2 text-primary"></i> CSV (Planilla)</button></li>
                            <li><button className="dropdown-item py-2" onClick={() => { handleExport('json'); setShowExportMenu(false); }}><i className="bi bi-filetype-json me-2 text-warning"></i> JSON</button></li>
                            <li><button className="dropdown-item py-2" onClick={() => { handleExport('geojson'); setShowExportMenu(false); }}><i className="bi bi-geo-alt-fill me-2 text-info"></i> GeoJSON</button></li>
                            <li><button className="dropdown-item py-2" onClick={() => { handleExport('pdf'); setShowExportMenu(false); }}><i className="bi bi-file-pdf-fill me-2 text-danger"></i> Reporte PDF</button></li>
                        </ul>
                    </div>
                </div>

                <div className="search-wrapper flex-grow-1" style={{ minWidth: '200px' }}>
                    <i className="bi bi-search"></i>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Buscar por nombre, documento..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>

                <div className="d-flex gap-2 flex-wrap align-items-center">
                    {currentUserRole === "admin" && (
                        <select
                            className="form-select"
                            style={{ width: 'auto' }}
                            value={originFilter}
                            onChange={(e) => setOriginFilter(e.target.value)}
                        >
                            <option value="all">Todo Origen</option>
                            <option value="laboratorio">Laboratorio</option>
                            <option value="investigacion">Investigación</option>
                        </select>
                    )}

                    <select
                        className="form-select"
                        style={{ width: 'auto', minWidth: '150px' }}
                        value={labFilter}
                        onChange={(e) => setLabFilter(e.target.value)}
                    >
                        <option value="all">Todos los Labs</option>
                        {[...new Set(pacientes.map(p => p.laboratorio_nombre))].filter(Boolean).sort().map((lab, idx) => (
                            <option key={idx} value={lab}>{lab}</option>
                        ))}
                    </select>

                    <select
                        className="form-select"
                        style={{ width: 'auto' }}
                        value={filterMunicipio}
                        onChange={(e) => setFilterMunicipio(e.target.value)}
                    >
                        <option value="">Todas las Ubicaciones</option>
                        {[...new Set(pacientes.map(p => p.municipio))].filter(Boolean).sort().map(m => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>

                    <select
                        className="form-select"
                        style={{ width: 'auto' }}
                        value={sexFilter}
                        onChange={(e) => setSexFilter(e.target.value)}
                    >
                        <option value="all">Todos los Géneros</option>
                        <option value="M">Masculino</option>
                        <option value="F">Femenino</option>
                    </select>

                    <select
                        className="form-select"
                        style={{ width: 'auto' }}
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                    >
                        <option value="asc">A-Z (Nombre)</option>
                        <option value="desc">Z-A (Nombre)</option>
                    </select>
                </div>
            </div>

            {/* Tabla Principal de Pacientes */}
            <div className="table-container">
                <div className="table-responsive">
                    <table className="table admin-table align-middle">
                        <thead>
                            <tr>
                                <th>Paciente / Identificación</th>
                                <th>Sexo / Edad</th>
                                <th>Ubicación / Dirección</th>
                                <th>Laboratorio</th>
                                <th>Fecha Registro</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status"></div>
                                        <p className="mt-2 text-muted">Cargando pacientes registrados...</p>
                                    </td>
                                </tr>
                            ) : filteredPacientes.length > 0 ? (
                                filteredPacientes.map((p) => (
                                    <tr key={p.id}>
                                        <td>
                                            <span className="lab-name">{p.nombre}</span>
                                            <span className="lab-email">{p.documento || 'Sin ID'}</span>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className={`badge ${p.sexo === 'M' ? 'bg-primary' : 'bg-danger'}`}>
                                                    {p.sexo}
                                                </span>
                                                <span className="fw-semibold">{p.edad} años</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="lab-name" style={{ fontSize: '0.85rem' }}>{p.municipio}</span>
                                            <span className="lab-email text-truncate" style={{ maxWidth: '200px' }}>{p.direccion}</span>
                                        </td>
                                        <td>
                                            <span className="lab-name" style={{ fontSize: '0.85rem' }}>{p.laboratorio_nombre || 'N/A'}</span>
                                            <span className="badge bg-light text-muted border" style={{ fontSize: '0.7rem' }}>{p.laboratorio_codigo || 'N/A'}</span>
                                        </td>
                                        <td>
                                            <span className="text-muted small">
                                                {new Date(p.created_at).toLocaleDateString()}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-btns">
                                                <button
                                                    className="action-btn btn-edit"
                                                    title="Ver Historial Clínico"
                                                    onClick={() => handleVerHistorial(p)}
                                                    disabled={loadingHistorial && selectedPaciente?.id === p.id}
                                                >
                                                    {loadingHistorial && selectedPaciente?.id === p.id ? (
                                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                                    ) : (
                                                        <i className="bi bi-clock-history"></i>
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-5 text-muted">
                                        No se encontraron pacientes registrados.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de Historial Clínico (Línea de Tiempo) */}
            <div className="modal fade" id="historialModal" tabIndex="-1" aria-labelledby="historialModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content border">
                        <div className="modal-header bg-primary text-white py-3">
                            <h5 className="modal-title fw-bold" id="historialModalLabel">
                                <i className="bi bi-journal-medical me-2"></i>
                                Historial Clínico: {selectedPaciente?.nombre}
                            </h5>
                            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Cerrar"></button>
                        </div>
                        <div className="modal-body p-4" style={{ backgroundColor: '#f8f9fa' }}>
                            {selectedPaciente && (
                                <div className="patient-quick-info mb-4 p-3 bg-white border">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <p className="mb-1 text-muted small uppercase fw-bold">Documento</p>
                                            <p className="mb-0 fw-semibold">{selectedPaciente.documento || 'No registrado'}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p className="mb-1 text-muted small uppercase fw-bold">Ubicación</p>
                                            <p className="mb-0 fw-semibold">{selectedPaciente.municipio}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <h6 className="fw-bold mb-3 text-secondary text-uppercase" style={{ fontSize: '0.8rem', letterSpacing: '1px' }}>
                                Línea de Tiempo de Exámenes
                            </h6>

                            {historial.length > 0 ? (
                                <div className="timeline-wrapper">
                                    {historial.map((c, idx) => (
                                        <div key={c.id} className="timeline-item d-flex gap-3 mb-4">
                                            <div className="timeline-date-marker d-flex flex-column align-items-center">
                                                <div className={`bg-${c.nivel_peligrosidad === 'ALTO' ? 'danger' : c.nivel_peligrosidad === 'MEDIO' ? 'warning' : 'success'}`} style={{ width: '12px', height: '12px', marginTop: '8px', zIndex: 2 }}></div>
                                                {idx !== historial.length - 1 && <div className="timeline-line bg-secondary opacity-25" style={{ width: '2px', flexGrow: 1, marginTop: '4px' }}></div>}
                                            </div>
                                            <div className="timeline-content bg-white p-3 border flex-grow-1">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div>
                                                        <span className="badge bg-light text-dark border me-2">
                                                            <i className="bi bi-calendar3 me-1"></i>
                                                            {(() => {
                                                                if (!c.fecha_registro) return "N/A";
                                                                const [y, m, d] = c.fecha_registro.split('-');
                                                                return `${d}/${m}/${y}`;
                                                            })()}
                                                        </span>
                                                        <span className={`badge ${c.nivel_peligrosidad === 'ALTO' ? 'bg-danger' : c.nivel_peligrosidad === 'MEDIO' ? 'bg-warning text-dark' : 'bg-success'}`}>
                                                            Riesgo {c.nivel_peligrosidad}
                                                        </span>
                                                    </div>
                                                    <span className="text-muted small">Examen #{c.id}</span>
                                                </div>
                                                <div className="mb-2">
                                                    <p className="mb-1 small text-muted fw-bold">Parásitos Detectados:</p>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {Array.isArray(c.parasitos_detectados) && c.parasitos_detectados.length > 0 ? (
                                                            c.parasitos_detectados.map((p, i) => (
                                                                <span key={i} className="badge bg-info text-dark" style={{ fontSize: '0.7rem' }}>{p}</span>
                                                            ))
                                                        ) : (
                                                            <span className="text-muted italic small">No se detectaron parásitos</span>
                                                        )}
                                                    </div>
                                                </div>
                                                {c.observaciones && (
                                                    <div className="mt-2 p-2 bg-light border">
                                                        <p className="mb-0 small text-dark"><i className="bi bi-chat-left-text me-2 text-muted"></i>{c.observaciones}</p>
                                                    </div>
                                                )}
                                                <div className="mt-2 pt-2 border-top d-flex justify-content-between align-items-center">
                                                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        <i className="bi bi-hospital me-1"></i>
                                                        Laboratorio: {c.laboratorio_nombre || 'Desconocido'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-5 bg-white border">
                                    <i className="bi bi-folder-x display-4 text-muted"></i>
                                    <p className="mt-3 text-muted">No hay exámenes registrados para este paciente.</p>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer bg-light border-0">
                            <button type="button" className="btn btn-secondary px-4 fw-bold" data-bs-dismiss="modal">Cerrar Historial</button>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminPatients;
